import React, { useState, useEffect } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { uploadFileToS3 } from "../services/files";
import { getEventsByType, getEventDates, getUploadSchema } from "../services/catalog";
import "../styles/Modal.css";

const Modal = ({ isOpen, onClose, eventTypeData }) => {
  // Estados para los datos de los Dropdowns
  const [eventsList, setEventsList] = useState([]);
  const [datesList, setDatesList] = useState([]);
  const [schema, setSchema] = useState(null);

  // Estados de la selección del usuario
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedDateObj, setSelectedDateObj] = useState(null); // Guardamos el objeto entero (id, venue, city)

  // Estados de carga y UI
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState("");

  // ✅ NUEVO: tipo de mensaje para colorear bien
  const [messageType, setMessageType] = useState("info"); // "info" | "success" | "error"

  // 1. Cuando se abre el modal, cargamos la API 2 (Eventos por Tipo)
  useEffect(() => {
    if (isOpen && eventTypeData) {
      setLoadingEvents(true);
      // Limpiamos estados anteriores
      setEventsList([]);
      setDatesList([]);
      setSelectedEvent("");
      setSelectedDateObj(null);
      setSchema(null);
      setSelectedFile(null);
      setUploadProgress(0);

      // ✅ reset mensaje
      setMessage("");
      setMessageType("info");

      getEventsByType(eventTypeData.event_type)
        .then(data => setEventsList(data))
        .catch(err => {
          setMessage("Error cargando eventos");
          setMessageType("error");
        })
        .finally(() => setLoadingEvents(false));

      // Cargar esquema base (API 4)
      getUploadSchema(eventTypeData.default_area)
        .then(config => setSchema(config))
        .catch(() => {
          // si falla el schema no bloquea, pero avisa
          setMessage("Error cargando configuración de carga");
          setMessageType("error");
        });
    }
  }, [isOpen, eventTypeData]);

  // 2. Cuando selecciona un Evento, cargamos la API 3 (Fechas)
  const handleEventChange = async (e) => {
    const eventId = e.target.value;
    setSelectedEvent(eventId);
    setSelectedDateObj(null); // Reset fecha
    setDatesList([]);
    setSelectedFile(null);
    setUploadProgress(0);

    setMessage("");
    setMessageType("info");

    if (eventId) {
      setLoadingDates(true);
      try {
        const dates = await getEventDates(eventId);
        setDatesList(dates);
      } catch (error) {
        setMessage("Error cargando fechas");
        setMessageType("error");
      } finally {
        setLoadingDates(false);
      }
    }
  };

  // 3. Manejo de selección de fecha
  const handleDateChange = (e) => {
    const dateId = e.target.value;
    const dateObj = datesList.find(d => d.event_date_id === dateId);
    setSelectedDateObj(dateObj);
    setSelectedFile(null);
    setUploadProgress(0);

    setMessage("");
    setMessageType("info");
  };

  // 4. Validaciones y Subida
  const handleFileUpload = async () => {
    if (!selectedFile || !selectedEvent || !selectedDateObj) {
      alert("Por favor completa todos los campos.");
      return;
    }

    // Validación de extensión (API 4)
    if (schema?.allowed_extensions) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (!schema.allowed_extensions.includes(ext)) {
        alert(`Extensión no permitida. Permitidas: ${schema.allowed_extensions.join(", ")}`);
        return;
      }
    }

    setUploading(true);
    setMessage("Iniciando carga...");
    setMessageType("info");

    try {
      // Construimos una ruta dinámica y organizada
      const eventObj = eventsList.find(ev => ev.event_id === selectedEvent);
      const eventNameSafe = eventObj.display_name.replace(/\s+/g, "_");
      const folderPath = `${eventTypeData.event_type}/${eventNameSafe}/${selectedDateObj.date}_${selectedDateObj.city}`;

      await uploadFileToS3(selectedFile, folderPath, (percent) => {
        setUploadProgress(percent);
      });

      setMessage("¡Archivo cargado exitosamente!");
      setMessageType("success"); // ✅ verde

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setMessage("Error al subir el archivo.");
      setMessageType("error"); // ✅ rojo solo si falla
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Cargar material: {eventTypeData.label}</h2>
          {schema?.help_text && <small className="help-text">{schema.help_text}</small>}
        </div>

        <div className="modal-body">
          {/* Dropdown 1: Artista / Evento */}
          <div className="form-group">
            <label>Selecciona Evento/Artista:</label>
            <select
              value={selectedEvent}
              onChange={handleEventChange}
              disabled={loadingEvents || uploading}
            >
              <option value="">-- Seleccionar --</option>
              {eventsList.map(ev => (
                <option key={ev.event_id} value={ev.event_id}>
                  {ev.display_name} ({ev.kind})
                </option>
              ))}
            </select>
            {loadingEvents && <span className="loader-text">Cargando...</span>}
          </div>

          {/* Dropdown 2: Fechas */}
          <div className="form-group">
            <label>Selecciona Fecha:</label>
            <select
              value={selectedDateObj?.event_date_id || ""}
              onChange={handleDateChange}
              disabled={!selectedEvent || loadingDates || uploading}
            >
              <option value="">-- Seleccionar Fecha --</option>
              {datesList.map(d => (
                <option key={d.event_date_id} value={d.event_date_id}>
                  {d.date} — {d.venue} ({d.city})
                </option>
              ))}
            </select>
            {loadingDates && <span className="loader-text">Buscando fechas...</span>}
          </div>

          {/* Input de Archivo */}
          {selectedDateObj && (
            <div className="upload-area">
              <input
                type="file"
                id="file-input"
                onChange={(e) => {
                  setSelectedFile(e.target.files[0]);
                  setUploadProgress(0);
                  setMessage("");
                  setMessageType("info");
                }}
                style={{ display: "none" }}
              />
              <label htmlFor="file-input" className="file-drop-zone">
                <FaCloudUploadAlt className="upload-icon-large" />
                <p>{selectedFile ? selectedFile.name : "Clic aquí para seleccionar archivo"}</p>
              </label>
            </div>
          )}

          {/* Barra de Progreso */}
          {uploading && (
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                {uploadProgress}%
              </div>
            </div>
          )}

          {/* ✅ Mensaje con tipo real */}
          {message && (
            <p className={`status-message ${messageType}`}>
              {message}
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="button-cancel" onClick={onClose} disabled={uploading}>
            Cancelar
          </button>
          <button
            className="button-send"
            onClick={handleFileUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? "Subiendo..." : "Subir Archivo"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
